import {
    useCallback,
    useEffect,
    useState
} from "react";
import {
    useWindowDimensions,
    ActivityIndicator,
    View
} from "react-native";
import type ContactRequestsProps from "./type";
import stylesheet from "./stylesheet";
import {
    NCoreUIKitLocalize,
    NCoreUIKitSnackBar,
    NCoreUIKitTheme,
    TextInput,
    Sticker,
    Button,
    Dialog,
    Text
} from "ncore-ui-kit";
import type {
    ContactFormItemType
} from "../../../../services/restAPI/actions/getAllContactForms/type";
import {
    MessageSquareIcon,
    CalendarIcon,
    SearchIcon,
    Trash2Icon,
    CheckIcon,
    PhoneIcon,
    MailIcon,
    UserIcon,
    EyeIcon
} from "lucide-react-native";
import moment from "moment";
import RESTServiceContext from "../../../../services/restAPI/context";
import Pagination from "../../../../components/pagination";
import ContactDetailModal from "../contactDetailModal";

const ContactRequests = ({
    refreshKey = 0
}: ContactRequestsProps) => {
    const {
        width
    } = useWindowDimensions();
    const isMobile = width < 768;

    const {
        radiuses,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext();

    const {
        localize
    } = NCoreUIKitLocalize.useContext();

    const [
        items,
        setItems
    ] = useState<ContactFormItemType[]>([]);

    const [
        loading,
        setLoading
    ] = useState<boolean>(true);

    const [
        actionLoading,
        setActionLoading
    ] = useState<boolean>(false);

    const [
        totalCount,
        setTotalCount
    ] = useState<number>(0);

    const [
        page,
        setPage
    ] = useState<number>(1);

    const [
        search,
        setSearch
    ] = useState<string>("");

    const [
        statusFilter,
        setStatusFilter
    ] = useState<"all" | "pending" | "resolved">("all");

    const [
        selectedItem,
        setSelectedItem
    ] = useState<ContactFormItemType | null>(null);

    const [
        isDetailModalVisible,
        setIsDetailModalVisible
    ] = useState<boolean>(false);

    const [
        isResolveDialogOpen,
        setIsResolveDialogOpen
    ] = useState<boolean>(false);

    const [
        resolveTargetId,
        setResolveTargetId
    ] = useState<string | null>(null);

    const [
        isDeleteDialogOpen,
        setIsDeleteDialogOpen
    ] = useState<boolean>(false);

    const [
        deleteTargetId,
        setDeleteTargetId
    ] = useState<string | null>(null);

    const fetchData = useCallback(async (
        pageIndex: number,
        searchQuery: string,
        currentStatus: "all" | "pending" | "resolved"
    ) => {
        setLoading(true);
        try {
            const res = await RESTServiceContext.action("getAllContactForms", {
                status: currentStatus === "all" ? undefined : currentStatus,
                search: searchQuery.trim() || undefined,
                page: pageIndex
            });

            if (res.payload) {
                const payloadItems = Array.isArray(res.payload) ? res.payload : [];
                setItems(payloadItems);
                setTotalCount(res.totalCount || payloadItems.length || 0);
            }
        } catch {
            NCoreUIKitSnackBar.open({
                title: localize("requests-action-error")
            });
        } finally {
            setLoading(false);
        }
    }, [
        localize
    ]);

    useEffect(() => {
        fetchData(page, search, statusFilter);
    }, [
        statusFilter,
        refreshKey,
        fetchData,
        search,
        page
    ]);

    const handleInspect = async (item: ContactFormItemType) => {
        setSelectedItem(item);
        setIsDetailModalVisible(true);

        if(!item.isRead) {
            try {
                await RESTServiceContext.actions.markContactFormAsRead({
                    id: item.id
                });
                setItems((prev) => prev.map((it) => it.id === item.id ? {
                    ...it,
                    isRead: true
                } : it));
            } catch(error) {
                void error;
            }
        }
    };

    const handleOpenResolve = (id: string) => {
        setResolveTargetId(id);
        setIsResolveDialogOpen(true);
    };

    const handleConfirmResolve = async () => {
        if(!resolveTargetId) {
            return;
        }

        setActionLoading(true);
        try {
            const res = await RESTServiceContext.actions.resolveContactForm({
                id: resolveTargetId
            });

            if(res.code === 200 && !res.isError) {
                NCoreUIKitSnackBar.open({
                    title: localize("requests-contact-resolve-success")
                });
                setIsResolveDialogOpen(false);
                setResolveTargetId(null);
                if(isDetailModalVisible) {
                    setIsDetailModalVisible(false);
                    setSelectedItem(null);
                }
                fetchData(page, search, statusFilter);
            } else {
                NCoreUIKitSnackBar.open({
                    title: res.message || localize("requests-action-error")
                });
            }
        } catch {
            NCoreUIKitSnackBar.open({
                title: localize("requests-action-error")
            });
        } finally {
            setActionLoading(false);
        }
    };

    const handleDelete = async () => {
        if(!deleteTargetId) {
            return;
        }

        setActionLoading(true);
        try {
            const res = await RESTServiceContext.actions.deleteContactForm({
                id: deleteTargetId
            });

            if(res.code === 200) {
                NCoreUIKitSnackBar.open({
                    title: localize("requests-contact-delete-success")
                });
                setIsDeleteDialogOpen(false);
                setDeleteTargetId(null);
                fetchData(page, search, statusFilter);
            } else {
                NCoreUIKitSnackBar.open({
                    title: localize("requests-action-error")
                });
            }
        } catch {
            NCoreUIKitSnackBar.open({
                title: localize("requests-action-error")
            });
        } finally {
            setActionLoading(false);
        }
    };

    const renderToolbar = () => {
        return <View
            style={[
                stylesheet.toolbarContainer,
                {
                    alignItems: isMobile ? "stretch" : "center",
                    flexDirection: isMobile ? "column" : "row",
                    gap: spaces.spacingMd
                }
            ]}
        >
            <View
                style={[
                    stylesheet.searchInputContainer,
                    {
                        width: isMobile ? "100%" : undefined,
                        maxWidth: isMobile ? "100%" : 360
                    }
                ]}
            >
                <TextInput
                    placeholder={localize("requests-search-placeholder")}
                    customColor={colors.project.brandWhite}
                    icon={({
                        size
                    }) => <SearchIcon
                        color={colors.content.icon.mid}
                        size={size}
                    />}
                    key="contact-requests-search"
                    spreadBehaviour="stretch"
                    onChangeText={(val) => {
                        setSearch(val);
                        setPage(1);
                    }}
                    value={search}
                />
            </View>
            <View
                style={[
                    stylesheet.toolbarActionsContainer,
                    {
                        backgroundColor: colors.content.container.subtle,
                        alignSelf: isMobile ? "flex-start" : "auto",
                        borderRadius: radiuses.md,
                        padding: spaces.spacingXs,
                        gap: spaces.spacingXs,
                        flexWrap: "wrap"
                    }
                ]}
            >
                <Button
                    customTextColor={statusFilter === "all" ? colors.project.brandWhite : colors.content.text.mid}
                    customColor={statusFilter === "all" ? "gold" : undefined}
                    variant={statusFilter === "all" ? "filled" : "ghost"}
                    title={localize("requests-filter-all")}
                    onPress={() => {
                        setStatusFilter("all");
                        setPage(1);
                    }}
                    spreadBehaviour="free"
                    type="neutral"
                />
                <Button
                    customTextColor={statusFilter === "pending" ? colors.project.brandWhite : colors.content.text.mid}
                    customColor={statusFilter === "pending" ? "gold" : undefined}
                    variant={statusFilter === "pending" ? "filled" : "ghost"}
                    title={localize("requests-filter-pending")}
                    onPress={() => {
                        setStatusFilter("pending");
                        setPage(1);
                    }}
                    spreadBehaviour="free"
                    type="neutral"
                />
                <Button
                    customTextColor={statusFilter === "resolved" ? colors.project.brandWhite : colors.content.text.mid}
                    customColor={statusFilter === "resolved" ? "gold" : undefined}
                    variant={statusFilter === "resolved" ? "filled" : "ghost"}
                    title={localize("requests-contact-status-resolved")}
                    onPress={() => {
                        setStatusFilter("resolved");
                        setPage(1);
                    }}
                    spreadBehaviour="free"
                    type="neutral"
                />
            </View>
        </View>;
    };

    const renderCard = (item: ContactFormItemType) => {
        const isItemAuth = Boolean(item.isAuth ?? item.isAuthenticated);
        const messageContent = item.message || item.description || "";

        return <View
            style={[
                stylesheet.contactCardContainer,
                {
                    alignItems: isMobile ? "flex-start" : "center",
                    flexDirection: isMobile ? "column" : "row",
                    backgroundColor: colors.project.brandWhite,
                    borderColor: colors.content.border.subtle,
                    borderWidth: borders.seperator,
                    borderRadius: radiuses.lg,
                    padding: spaces.spacingLg,
                    gap: spaces.spacingLg
                }
            ]}
            key={item.id}
        >
            <View
                style={[
                    stylesheet.cardDetailsContainer,
                    {
                        gap: spaces.spacingSm
                    }
                ]}
            >
                <View
                    style={[
                        stylesheet.cardTitleContainer,
                        {
                            gap: spaces.spacingSm
                        }
                    ]}
                >
                    <UserIcon
                        color={colors.content.icon.high}
                        size={18}
                    />
                    <Text
                        customColor={colors.content.text.high}
                        style={stylesheet.fullNameText}
                        variant="titleSmallSize"
                    >
                        {item.fullName}
                    </Text>
                    <Sticker
                        title={isItemAuth ? localize("requests-contact-badge-authenticated") : localize("requests-contact-badge-guest")}
                        type={isItemAuth ? "success" : "neutral"}
                        size="small"
                    />
                    <Sticker
                        title={item.status === "resolved" ? localize("requests-contact-status-resolved") : localize("requests-contact-status-pending")}
                        type={item.status === "resolved" ? "success" : "warning"}
                        size="small"
                    />
                    {
                        !item.isRead ?
                            <Sticker
                                title={localize("requests-contact-badge-unread")}
                                type="danger"
                                size="small"
                            />
                        :
                            null
                    }
                </View>
                <View
                    style={[
                        stylesheet.cardDateContainer,
                        {
                            gap: spaces.spacingXs
                        }
                    ]}
                >
                    <CalendarIcon
                        color={colors.content.icon.low}
                        size={14}
                    />
                    <Text
                        customColor={colors.content.text.low}
                        variant="labelSmallSize"
                    >
                        {moment(item.createdAt).format("DD.MM.YYYY HH:mm")}
                    </Text>
                </View>
                <Text
                    customColor={colors.content.text.high}
                    style={stylesheet.subjectText}
                    variant="bodyMediumSize"
                >
                    {item.subject}
                </Text>
                {Boolean(messageContent) && <Text
                    customColor={colors.content.text.mid}
                    variant="bodySmallSize"
                    numberOfLines={2}
                >
                    {messageContent}
                </Text>}
                <View
                    style={[
                        stylesheet.cardContactContainer,
                        {
                            gap: spaces.spacingLg
                        }
                    ]}
                >
                    <View
                        style={[
                            stylesheet.contactInfoItemRow,
                            {
                                gap: spaces.spacingXs
                            }
                        ]}
                    >
                        <MailIcon
                            color={colors.content.icon.mid}
                            size={14}
                        />
                        <Text
                            customColor={colors.content.text.mid}
                            variant="labelSmallSize"
                        >
                            {item.mail}
                        </Text>
                    </View>
                    {Boolean(item.phoneNumber) && <View
                        style={[
                            stylesheet.contactInfoItemRow,
                            {
                                gap: spaces.spacingXs
                            }
                        ]}
                    >
                        <PhoneIcon
                            color={colors.content.icon.mid}
                            size={14}
                        />
                        <Text
                            customColor={colors.content.text.mid}
                            variant="labelSmallSize"
                        >
                            {item.phoneNumber}
                        </Text>
                    </View>}
                </View>
            </View>
            <View
                style={[
                    stylesheet.cardActionsContainer,
                    {
                        justifyContent: isMobile ? "flex-end" : "flex-start",
                        alignSelf: isMobile ? "flex-end" : "auto",
                        width: isMobile ? "100%" : undefined,
                        gap: spaces.spacingSm
                    }
                ]}
            >
                <Button
                    title={localize("requests-action-inspect")}
                    customTextColor={colors.project.brandWhite}
                    icon={({
                        size
                    }) => <EyeIcon
                        color={colors.project.brandWhite}
                        size={size}
                    />}
                    customBorderColor="secondaryButton"
                    onPress={() => handleInspect(item)}
                    customColor="secondaryButton"
                    spreadBehaviour="free"
                    variant="filled"
                    size="medium"
                />
                {
                    item.status !== "resolved" ?
                        <Button
                            title={localize("requests-contact-resolve")}
                            onPress={() => handleOpenResolve(item.id)}
                            icon={({
                                color,
                                size
                            }) => <CheckIcon
                                color={colors.content.icon[color]}
                                size={size}
                            />}
                            spreadBehaviour="free"
                            variant="filled"
                            type="success"
                            size="medium"
                        />
                    :
                        null
                }
                <Button
                    icon={({
                        color,
                        size
                    }) => <Trash2Icon
                        color={colors.content.icon[color]}
                        size={size}
                    />}
                    onPress={() => {
                        setDeleteTargetId(item.id);
                        setIsDeleteDialogOpen(true);
                    }}
                    title={localize("delete")}
                    spreadBehaviour="free"
                    variant="filled"
                    size="medium"
                    type="danger"
                />
            </View>
        </View>;
    };

    const renderContent = () => {
        if(loading) {
            return <View
                style={stylesheet.loadingContainer}
            >
                <ActivityIndicator
                    color={colors.content.icon.high}
                    size="large"
                />
            </View>;
        }

        if(items.length === 0) {
            return <View
                style={[
                    stylesheet.emptyContainer,
                    {
                        backgroundColor: colors.project.brandWhite,
                        borderColor: colors.content.border.subtle,
                        borderWidth: borders.seperator,
                        borderRadius: radiuses.lg,
                        padding: spaces.spacingXl,
                        gap: spaces.spacingMd
                    }
                ]}
            >
                <MessageSquareIcon
                    color={colors.content.icon.low}
                    size={40}
                />
                <Text
                    customColor={colors.content.text.mid}
                    variant="bodyMediumSize"
                >
                    {localize("requests-contact-empty")}
                </Text>
            </View>;
        }

        return <View
            style={[
                stylesheet.listContainer,
                {
                    gap: spaces.spacingMd
                }
            ]}
        >
            {items.map(renderCard)}
        </View>;
    };

    const renderPagination = () => {
        if(totalCount <= 20) {
            return null;
        }

        return <View
            style={[
                stylesheet.paginationContainer,
                {
                    marginTop: spaces.spacingXl
                }
            ]}
        >
            <Pagination
                onSelect={(item) => {
                    setPage(item.pageNumber);
                }}
                totalDataCount={totalCount}
                currentPage={page}
                itemPerPage={20}
            />
        </View>;
    };

    return <View
        style={[
            stylesheet.container,
            {
                gap: spaces.spacingLg
            }
        ]}
    >
        {renderToolbar()}
        {renderContent()}
        {renderPagination()}
        <ContactDetailModal
            onClose={() => {
                setIsDetailModalVisible(false);
                setSelectedItem(null);
            }}
            isVisible={isDetailModalVisible}
            onResolve={handleOpenResolve}
            item={selectedItem}
        />
        <Dialog
            onOverlayPress={() => setIsResolveDialogOpen(false)}
            content={localize("requests-contact-resolve-desc")}
            secondaryButtonProps={{
                onPress: () => setIsResolveDialogOpen(false),
                title: localize("cancel")
            }}
            primaryButtonProps={{
                title: localize("requests-contact-resolve"),
                onPress: handleConfirmResolve,
                isLoading: actionLoading
            }}
            onClosed={() => setIsResolveDialogOpen(false)}
            isVisible={isResolveDialogOpen}
            variant="yes-no"
        />
        <Dialog
            content={localize("requests-contact-delete-confirm-desc")}
            onOverlayPress={() => setIsDeleteDialogOpen(false)}
            secondaryButtonProps={{
                onPress: () => setIsDeleteDialogOpen(false),
                title: localize("cancel")
            }}
            onClosed={() => setIsDeleteDialogOpen(false)}
            isVisible={isDeleteDialogOpen}
            primaryButtonProps={{
                title: localize("delete"),
                isLoading: actionLoading,
                onPress: handleDelete
            }}
            variant="yes-no"
        />
    </View>;
};
export default ContactRequests;
